import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

function Stars({ value, size = 4, interactive = false, onChange = null, testIdPrefix = "star" }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= Math.round(value);
        const cls = `w-${size} h-${size} ${filled ? "text-[#FFB800] fill-[#FFB800]" : "text-zinc-700"}`;
        const Tag = interactive ? "button" : "span";
        return (
          <Tag
            key={n}
            type="button"
            data-testid={interactive ? `${testIdPrefix}-${n}` : undefined}
            onClick={interactive ? () => onChange?.(n) : undefined}
            className={interactive ? "p-0.5 hover:scale-110 transition-transform" : ""}
          >
            <Star className={cls} strokeWidth={1.5} />
          </Tag>
        );
      })}
    </div>
  );
}

export default function ReviewSection({ product, autofocus = false }) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [eligibility, setEligibility] = useState({ can_review: false, already_reviewed: false });
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/products/${product.id}/reviews`);
    setReviews(data || []);
    if (user) {
      try {
        const { data: e } = await api.get(`/products/${product.id}/review-eligibility`);
        setEligibility(e);
      } catch { /* ignore */ }
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [product.id, user?.id]);

  useEffect(() => {
    if (autofocus && eligibility.can_review) {
      const el = document.getElementById("review-form");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [autofocus, eligibility.can_review]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/products/${product.id}/reviews`, { rating, comment });
      toast.success("Review posted");
      setComment("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post review");
    } finally { setSubmitting(false); }
  };

  const avg = product.avg_rating || 0;
  const count = product.review_count || reviews.length;

  return (
    <section id="reviews" data-testid="reviews-section" className="mt-20">
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="font-display font-black text-3xl tracking-tight">Reviews</h2>
        <div className="flex items-center gap-3">
          <Stars value={avg} />
          <span className="text-sm text-zinc-300">{avg ? avg.toFixed(1) : "—"}</span>
          <span className="text-xs text-zinc-500 uppercase tracking-widest">{count} review{count === 1 ? "" : "s"}</span>
        </div>
      </div>
      <p className="text-xs text-[#CCFF00] uppercase tracking-widest mb-8 inline-flex items-center gap-1" data-testid="reviews-verified-note">
        <ShieldCheck className="w-3 h-3" /> 100% verified — only delivered-order buyers can leave a review.
      </p>

      {/* Write a review */}
      {user && eligibility.can_review && (
        <form id="review-form" onSubmit={submit} data-testid="review-form" className="card-surface p-6 mb-8 border-[#CCFF00]/30">
          <p className="text-xs uppercase tracking-widest text-[#CCFF00] mb-4">Leave a review</p>
          <div className="flex items-center gap-3 mb-4">
            <Stars value={rating} interactive onChange={setRating} size={6} testIdPrefix="rating-star" />
            <span className="text-sm text-zinc-300">{rating} / 5</span>
          </div>
          <textarea
            data-testid="review-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Tell other buyers what you loved (or didn't)…"
            rows={4}
            className="input-dark resize-y w-full"
            maxLength={2000}
          />
          <div className="flex justify-end mt-4">
            <button data-testid="review-submit" disabled={submitting} className="btn-primary">{submitting ? "Posting…" : "Post review"}</button>
          </div>
        </form>
      )}

      {user && eligibility.already_reviewed && (
        <p className="text-xs text-zinc-500 mb-6" data-testid="already-reviewed">You've already reviewed this product. Thanks!</p>
      )}

      {/* List */}
      {reviews.length === 0 ? (
        <p className="text-zinc-500 text-sm" data-testid="reviews-empty">No reviews yet — be the first.</p>
      ) : (
        <ul className="space-y-4">
          {reviews.map((r) => (
            <li key={r.id} data-testid={`review-${r.id}`} className="card-surface p-5">
              <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                <div className="flex items-center gap-3 flex-wrap">
                  <Stars value={r.rating} />
                  <span className="text-sm font-bold text-white">{r.user_name}</span>
                  <span
                    data-testid={`verified-badge-${r.id}`}
                    title="This reviewer purchased and received the product"
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#CCFF00] border border-[#CCFF00]/40 px-2 py-1 font-bold"
                    style={{ boxShadow: "0 0 12px rgba(204,255,0,0.25)" }}
                  >
                    <ShieldCheck className="w-3 h-3" /> Verified delivery
                  </span>
                </div>
                <span className="text-xs text-zinc-500">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
              {r.comment && <p className="text-sm text-zinc-300 whitespace-pre-line">{r.comment}</p>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
